<p-drawer [header]="header" [(visible)]="visible" [position]="position" (visibleChange)="onVisibleChange($event)" [style]="{'width': width}">
  <div class="flex flex-col gap-2">
    <div *ngFor="let field of fields" class="flex flex-col gap-2">
      <label [for]="field.name">
        {{ field.label }} 
        <span *ngIf="field.required" class="text-red-500">*</span>
      </label>
      
      <!-- Text Input -->
      <div *ngIf="field.type === 'text'" class="flex flex-col gap-1">
        <input 
          pInputText 
          [id]="field.name" 
          [placeholder]="field.placeholder || ''"
          [(ngModel)]="formData[field.name]"
          [attr.maxlength]="field.maxLength"
          [required]="field.required || false" />
        <small *ngIf="field.maxLength" class="text-gray-500 text-xs">
          {{ formData[field.name]?.length || 0 }} / {{ field.maxLength }} characters
        </small>
      </div>
      
      <!-- Quill Editor -->
      <p-editor 
        *ngIf="field.type === 'editor'"
        [id]="field.name"
        [(ngModel)]="formData[field.name]"
        [placeholder]="field.placeholder || 'Enter your content here...'"
        [style]="getEditorConfig(field).style"
        [formats]="getEditorConfig(field).formats"
        [modules]="getEditorConfig(field).modules"
        [required]="field.required || false">
        <ng-template #header>
          <span class="ql-formats">
            <button type="button" class="ql-bold" aria-label="Bold"></button>
            <button type="button" class="ql-italic" aria-label="Italic"></button>
            <button type="button" class="ql-underline" aria-label="Underline"></button>
          </span>
          <span class="ql-formats">
            <button type="button" class="ql-list" value="ordered" aria-label="Ordered List"></button>
            <button type="button" class="ql-list" value="bullet" aria-label="Bullet List"></button>
          </span>
          <span class="ql-formats">
            <button type="button" class="ql-link" aria-label="Link"></button>
          </span>
        </ng-template>
      </p-editor>
      
      <!-- File Upload-->
      <div *ngIf="field.type === 'file'" class="flex flex-col gap-1">
        <div class="card">
          <p-toast />
          
          <!-- Main File Upload with Drag & Drop -->
          <div 
            class="mb-4 p-4 border-2 border-dashed rounded-lg text-center drag-drop-area"
            [class]="dragOver ? 'border-blue-500 bg-blue-50 drag-over' : 'border-gray-300'"
            [class.loading]="isLoading"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event, field)">
            
            <input 
              type="file" 
              [accept]="field.accept || 'image/*'"
              (change)="onFileUpload($event)"
              class="hidden"
              [disabled]="isLoading"
              [id]="'file-input-' + field.name" />
            
            <div class="flex flex-col items-center justify-center py-4">
              <svg class="mb-2" width="38" height="27" viewBox="0 0 38 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.6875 26.875C4.0293 26.875 0.25 23.0957 0.25 18.4375C0.25 14.7578 2.60547 11.6289 5.88672 10.4746C5.88086 10.3164 5.875 10.1582 5.875 10C5.875 4.82031 10.0703 0.625 15.25 0.625C18.7246 0.625 21.7539 2.51172 23.377 5.32422C24.2676 4.72656 25.3457 4.375 26.5 4.375C29.6055 4.375 32.125 6.89453 32.125 10C32.125 10.7148 31.9902 11.3945 31.75 12.0273C35.1719 12.7188 37.75 15.748 37.75 19.375C37.75 23.5176 34.3926 26.875 30.25 26.875H8.6875ZM13.3164 14.1602C12.7656 14.7109 12.7656 15.6016 13.3164 16.1465C13.8672 16.6914 14.7578 16.6973 15.3027 16.1465L17.5879 13.8613V21.7188C17.5879 22.498 18.2148 23.125 18.9941 23.125C19.7734 23.125 20.4004 22.498 20.4004 21.7188V13.8613L22.6855 16.1465C23.2363 16.6973 24.127 16.6973 24.6719 16.1465C25.2168 15.5957 25.2227 14.7051 24.6719 14.1602L19.9844 9.47266C19.4336 8.92188 18.543 8.92188 17.998 9.47266L13.3105 14.1602H13.3164Z" fill="#9CA3AF"/>
                </svg>
              <label 
                [for]="'file-input-' + field.name" 
                class="cursor-pointer text-blue-600 hover:text-blue-800 font-medium"
                [class.opacity-50]="isLoading"
                [class.cursor-not-allowed]="isLoading">
                {{ isLoading ? 'Uploading...' : 'Click to upload or drag and drop' }}
              </label>
              <p class="text-sm text-gray-500 mt-2">
                {{ field.accept || 'image/*' }} (MAX. 1MB)
              </p>
            </div>
          </div>
          
          <!-- Image preview -->
          <div *ngIf="imagePreview" class="mb-4 file-preview">
            <h5>Preview</h5>
            <div class="flex justify-center items-center gap-4">
              <div class="w-full h-full">
                <img [src]="imagePreview" alt="Preview" class="object-contain border rounded" />
              </div>
              <p-button 
                label="Remove" 
                icon="pi pi-trash" 
                severity="danger" 
                size="small"
                [disabled]="isLoading"
                (click)="removeFile()" />
            </div>
          </div>
          
          <!-- Loading indicator -->
          <div *ngIf="isLoading" class="mb-4">
            <div class="flex justify-center items-center gap-2">
              <i class="pi pi-spin pi-spinner text-blue-600"></i>
              <span class="text-blue-600">Uploading image...</span>
            </div>
          </div>
        </div>
        <small class="text-gray-500">PNG, JPG (MAX. 1MB)</small>
      </div>
    </div>
    
    <p-button 
      [label]="submitLoading ? 'Submitting...' : submitLabel" 
      [icon]="submitLoading ? 'pi pi-spin pi-spinner' : ''"
      (click)="onSubmit()"
      [disabled]="!isFormValid() || submitLoading"
      [style]="{'width': '100%', 'margin-top': '10px'}" />
  </div>
</p-drawer>
